import { api } from "./api";
import { ICreatePaymentResponse } from "../types/utils.types";

// ─── Types ───────────────────────────────────────────────────────────────────

export type CreditType = "token" | "count" | "seat";

// One selectable feature for the package create/edit form. Served by
// GET FeatureCreditPackages/feature-options — a curated subset of FeatureKeys.All,
// not the full key list. creditType drives the display label.
export interface FeatureOption {
  featureKey: string;
  creditType: CreditType;
}

export interface FeatureCreditPackagePrice {
  currencyId: number;
  priceBeforeTax: number;
  vat: number;
  priceAfterTax: number;
}

export interface FeatureCreditPackageDto {
  id: number;
  name: string; // Hebrew display name
  nameEn: string; // English display name
  description: string | null;
  featureKey: string;
  creditType: CreditType; // derived server-side from featureKey (read-only)
  amount: number; // tokens / counts / seats granted per purchase
  companyId: number | null; // null = global pack; number = company-specific
  isActive: boolean;
  sortOrder: number;
  prices: FeatureCreditPackagePrice[];
}

export interface FeatureCreditPackageCreateDto {
  name: string;
  nameEn: string;
  description?: string | null;
  featureKey: string;
  amount: number;
  companyId?: number | null; // null = global
  isActive?: boolean; // default true
  sortOrder?: number;
  prices: FeatureCreditPackagePrice[]; // at most one per currencyId
}

// All fields optional; null/omitted = unchanged.
export type FeatureCreditPackageUpdateDto = Partial<FeatureCreditPackageCreateDto> & {
  isActive?: boolean;
};

export interface FeatureCreditBuyRequest {
  packageId: number;
  currencyId: number;
  scope: "self" | "company";
  targetUserId?: number | null;
}

// Backend returns the same shape as GetPaymentPage.
export type FeatureCreditBuyResponse = ICreatePaymentResponse;

// ─── Endpoints ───────────────────────────────────────────────────────────────

export const featureCreditPackagesApi = api.injectEndpoints({
  endpoints: (build) => ({
    listFeatureCreditPackages: build.query<FeatureCreditPackageDto[], boolean | void>({
      query: (includeInactive) => ({
        url: "FeatureCreditPackages",
        params: { includeInactive: !!includeInactive },
      }),
      providesTags: ["FeatureCreditPackages"],
    }),

    getFeatureOptions: build.query<FeatureOption[], void>({
      query: () => ({ url: "FeatureCreditPackages/feature-options" }),
    }),

    getFeatureCreditPackage: build.query<FeatureCreditPackageDto, number>({
      query: (id) => ({ url: `FeatureCreditPackages/${id}` }),
      providesTags: (_result, _error, id) => [{ type: "FeatureCreditPackages", id }],
    }),

    createFeatureCreditPackage: build.mutation<
      FeatureCreditPackageDto,
      FeatureCreditPackageCreateDto
    >({
      query: (body) => ({ url: "FeatureCreditPackages", method: "POST", body }),
      invalidatesTags: ["FeatureCreditPackages"],
    }),

    updateFeatureCreditPackage: build.mutation<
      FeatureCreditPackageDto,
      { id: number; body: FeatureCreditPackageUpdateDto }
    >({
      query: ({ id, body }) => ({ url: `FeatureCreditPackages/${id}`, method: "PUT", body }),
      invalidatesTags: ["FeatureCreditPackages"],
    }),

    deleteFeatureCreditPackage: build.mutation<void, number>({
      query: (id) => ({ url: `FeatureCreditPackages/${id}`, method: "DELETE" }),
      invalidatesTags: ["FeatureCreditPackages"],
    }),

    buyFeatureCredits: build.mutation<FeatureCreditBuyResponse, FeatureCreditBuyRequest>({
      query: (body) => ({ url: "FeatureCredits/Buy", method: "POST", body }),
    }),
  }),
});

export const {
  useGetFeatureOptionsQuery,
  useListFeatureCreditPackagesQuery,
  useGetFeatureCreditPackageQuery,
  useCreateFeatureCreditPackageMutation,
  useUpdateFeatureCreditPackageMutation,
  useDeleteFeatureCreditPackageMutation,
  useBuyFeatureCreditsMutation,
} = featureCreditPackagesApi;
